import { useEffect, useState } from 'react';

import { attach } from './Upload/internals.common';
import MediaUploadError from './Upload/MediaUploadError';
import UploadManager from './Upload/UploadManager';
import UploadTask from './Upload/UploadTask';
import { UPLOAD_WORKER_COUNT } from './config';
import {
  MediaType,
  RSDKMedia,
  UploadMediaHookAPI,
  UploadTaskConfig,
  UploadTaskRecord,
} from './types';
import useSelectMedia from './useSelectMedia';

const uploadManager = new UploadManager({ workerCount: UPLOAD_WORKER_COUNT });

export { getFilesFromSelectionEvent } from './internals';

export { useSelectMedia };

export const attachMedia: RSDKMedia['attachMedia'] = (task) => attach(task);

export const uploadMedia: RSDKMedia['uploadMedia'] = (tasks, listeners) =>
  uploadManager.add(tasks, listeners);

export const useUploadMedia: RSDKMedia['useUploadMedia'] = (opts) => {
  const persistedTasks =
    opts?.persistenceKey && uploadManager.getPersistedTasksForKey(opts.persistenceKey);
  const [uploadTasks, setUploadTasks] = useState<UploadTaskRecord>(persistedTasks || {});

  // used in many places below to update the uploadTasks state in response to any task callbacks
  const handleUploadTaskUpdate = (task: UploadTask): void => {
    setUploadTasks((currentTasks) => ({
      ...currentTasks,
      [task.file.id]: task,
    }));
  };

  const handleCanceledTask = (task: UploadTask): void => {
    handleUploadTaskUpdate(task); // start by updating the task like onProgress
  };

  const handleFinishedTask = (task: UploadTask): void => {
    handleUploadTaskUpdate(task); // start by updating the task like onProgress
  };

  const removeTaskFromPersistence = (taskId: string): void => {
    if (opts?.persistenceKey) {
      uploadManager.removePersistedTaskByKey(opts.persistenceKey, taskId);
    }
  };

  // on mount, reattach listeners to persisted tasks
  useEffect(() => {
    if (persistedTasks) {
      Object.values(persistedTasks).forEach((persistedTask) => {
        persistedTask.attachListeners({
          // don't worry about callbacks here, we just want to reattach the basic handlers
          // the original callbacks are still attached to the task
          onProgress: handleUploadTaskUpdate,
          onFinish: handleFinishedTask,
          onCancel: handleCanceledTask,
        });
      });
    }
    return () => {
      // Don't unattach listeners on unmount.
      // At this time we are relying on the behavior where callbacks are still called even if the hook is unmounted.
      // This is helpful to ActiveRoverCard where we want onMediaUploadSuccess & onMediaUploadFailed to be called even if the card is unmounted.
      // We don't care about cleaning up the various set*Tasks calls in the listeners because React handles that for us!
    };
    // we don't need exhaustive deps here because we only want to run this effect once on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadMedia: UploadMediaHookAPI['uploadMedia'] = (taskConfigs, callbacks = {}) => {
    let finishedTasks = 0;
    const total = taskConfigs.length;

    const finalTaskConfigs = opts?.persistenceKey
      ? taskConfigs.map((task) => ({
          ...task,
          opts: {
            ...task.opts,
            persistenceKey: opts.persistenceKey,
          },
        }))
      : taskConfigs;

    // this sends the taskConfigs to UploadManager which then creates new UploadTasks
    const tasks = uploadMedia(finalTaskConfigs, {
      onFinish: (task) => {
        if (task.isSuccessful() && callbacks.onUploadSuccess) {
          callbacks.onUploadSuccess?.(task);
        }
        if (task.isFailed() && callbacks.onUploadFailed) {
          callbacks.onUploadFailed?.(task);
        }
        if (task.isCancelled() && callbacks.onUploadCancelled) {
          callbacks.onUploadCancelled(task);
        }

        finishedTasks += 1;
        handleFinishedTask(task);

        if (finishedTasks === total && callbacks.onAllFinished) {
          callbacks.onAllFinished();
        }
      },
      onProgress: (task) => {
        handleUploadTaskUpdate(task); // update the task in the hook state
        if (task.isInProgress() && callbacks.onUploadProgress) {
          callbacks.onUploadProgress(task);
        }
      },
      onCancel: (task) => {
        finishedTasks += 1;
        handleCanceledTask(task);
        if (task.isCancelled() && callbacks.onUploadCancelled) {
          callbacks.onUploadCancelled(task);
        }
      },
    });
    setUploadTasks({ ...uploadTasks, ...tasks });
  };

  /** a generic handler for removing tasks that handles tasks in any status */
  const removeUploadTask = (taskId: string): void => {
    removeTaskFromPersistence(taskId);

    const task = uploadTasks[taskId];
    if (!task) return;

    // status specific handling
    if (task.isInProgress()) {
      task.cancel();
    }
    delete uploadTasks[taskId];
    setUploadTasks({ ...uploadTasks });
  };

  const retryUploadTasks: UploadMediaHookAPI['retryUploadTasks'] = (
    tasks,
    callbacks = {}
  ): void => {
    const newTaskConfigs: UploadTaskConfig[] = tasks.map((task) => ({
      file: task.file,
      opts: task.options,
    }));

    tasks.forEach((task) => {
      removeUploadTask(task.id);
    });

    handleUploadMedia(newTaskConfigs, callbacks);
  };

  // check for upload status and errors
  let error: MediaUploadError | undefined;
  let isUploading = false;
  Object.values(uploadTasks).forEach((task) => {
    if (task.isInProgress()) {
      isUploading = true;
    }
    // only grab the first errored task for the "merged" error output
    if (!error && task.result?.isErr()) {
      error = task.result.error;
    }
  });

  return {
    error,
    uploadTasks,
    uploadMedia: handleUploadMedia,
    retryUploadTasks,
    removeUploadTask,
    queuedAndInProgressTasks: Object.values(uploadTasks).filter((task) => task.isInProgress()),
    successfulUploadTasks: Object.values(uploadTasks).filter((task) => task.isSuccessful()),
    failedUploadTasks: Object.values(uploadTasks).filter((task) => task.isFailed()),
    isUploading,
  };
};

export const useSelectAndUploadMedia: RSDKMedia['useSelectAndUploadMedia'] = ({
  imageUploadOptions,
  selectOptions,
  uploadListeners,
  uploadOptions,
  persistenceKey,
}) => {
  const {
    error: uploadError,
    isUploading,
    uploadMedia: uploadMediaImperative,
    uploadTasks,
    queuedAndInProgressTasks,
    successfulUploadTasks,
    failedUploadTasks,
    retryUploadTasks,
    removeUploadTask,
  } = useUploadMedia({ persistenceKey });

  const {
    error: selectionError,
    inputProps,
    dropzoneProps,
    selectMedia,
  } = useSelectMedia({
    ...selectOptions,
    onSelectionSuccess: (mediaFiles) => {
      // attach image-specific options
      const tasks = mediaFiles.map((file) => ({
        file,
        opts: {
          ...uploadOptions,
          ...(file.type === MediaType.IMAGE ? { ...imageUploadOptions } : {}),
        },
      }));
      uploadMediaImperative(tasks, uploadListeners);
    },
  });

  return {
    dropzoneProps,
    error: selectionError || uploadError,
    inputProps,
    isUploading,
    selectMedia,
    uploadTasks,
    queuedAndInProgressTasks,
    failedUploadTasks,
    successfulUploadTasks,
    retryUploadTasks,
    removeUploadTask,
  };
};
